// src/pages/ProductsPageLazy.js
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import GridView from "../components/GridView";
import ListView from "../components/ListView";
import WsListView from "../components/WsListView";
import BannerSlider from "../components/BannerSlider";
import { BsGrid3X3GapFill, BsListUl, BsTable, BsFilter, BsArrowUp, BsArrowDown, BsArrowClockwise, BsX } from "react-icons/bs";
import { productService } from "../services/productService";
import { bannerService } from "../services/bannerService";
import { useSidebar } from "../context/SidebarContext";
import { useAuth } from "../context/AuthContext";
import { useCompanySettingsQuery } from "../hooks/useCompanySettings";
import { useCustomerContext } from "../context/CustomerContext";
import useAccess from "../hooks/useAccess";

// Define a key for localStorage persistence
const PRODUCT_PAGE_STATE_KEY = 'productPageState';
const DEFAULT_PAGE_SIZE = 150;
const MAX_PRODUCTS_IN_MEMORY = 1000;

const ProductsPageLazy = () => {
  // Get context from the Outlet
  const { searchQuery: contextSearchQuery, setSearchQuery, isMobile, headerHeight = 0 } = useOutletContext();
  
  // Get sidebar states and controls
  const { leftCollapsed, rightVisible, toggleLeftSidebar, toggleRightSidebar } = useSidebar();
  
  // Get company settings using the hook that handles auth fallback
  const { settings: companySettings, loading: settingsLoading } = useCompanySettingsQuery();

  const { user, isAuthenticated } = useAuth();
  const { can: canAccess } = useAccess();
  const can = (key) => !isAuthenticated || canAccess(key);
  const { selectedCustomer } = useCustomerContext();
  const catalogCustomerId = selectedCustomer?.id || null;
  
  // Get search parameters from URL
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Determine the effective search query (URL takes precedence over context)
  const urlSearchQuery = searchParams.get('search') || '';
  const urlCategoryFilter = searchParams.get('category') || '';
  const urlBadgeFilter = searchParams.get('badge') || '';
  const searchQuery = urlSearchQuery || contextSearchQuery;

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState([]);
  const [badges, setBadges] = useState([]);
  const [showFilters, setShowFilters] = useState(() => {
    // Auto-open filters on mobile if any filter is applied
    try {
      const saved = localStorage.getItem(PRODUCT_PAGE_STATE_KEY);
      if (saved) {
        const { sortField, sortDirection, badgeFilter } = JSON.parse(saved);
        const hasNonDefaultSort = sortField && sortField !== 'name';
        const hasNonDefaultDirection = sortDirection && sortDirection !== 'asc';
        const hasBadgeFilter = badgeFilter && badgeFilter !== '';
        return hasNonDefaultSort || hasNonDefaultDirection || hasBadgeFilter;
      }
    } catch { }
    return false;
  });
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const observerRef = useRef();
  const fetchRequestIdRef = useRef(0);
  const [pageSize, setPageSize] = useState(() => {
    try {
      const saved = localStorage.getItem(PRODUCT_PAGE_STATE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.pageSize) return parsed.pageSize;
      }
    } catch { }
    return DEFAULT_PAGE_SIZE;
  });

  // Load simple view settings from server
  const [viewSettings, setViewSettings] = useState({
    gridViewEnabled: true,
    listViewEnabled: true,
    wsListViewEnabled: true,
    hideOutOfStock: false,
    paginationEnabled: false,
    defaultView: 'grid',
    defaultSort: 'name'
  });

  // Banner state
  const [banners, setBanners] = useState([]);
  const [bannerLocationSettings, setBannerLocationSettings] = useState(null);

  // Derive hideOutOfStock/hideZeroPrice directly from companySettings to avoid race conditions
  // (viewSettings state lags one render behind companySettings)
  const hideOutOfStock = companySettings?.system_settings?.product_views?.hideOutOfStock ?? false;
  const hideZeroPrice = companySettings?.system_settings?.product_views?.hideZeroPrice ?? false;

  const stockVisibilityGroups = companySettings?.system_settings?.product_views?.stockVisibilityGroups ?? [];
  const showStock = (companySettings?.system_settings?.product_views?.showStockInCatalog ?? false) &&
    (stockVisibilityGroups.length === 0 ||
      (user?.groups || []).some(name => stockVisibilityGroups.some(g => g.name === name)));

  // Update view settings when company settings are loaded
  useEffect(() => {
    if (companySettings && companySettings.system_settings && companySettings.system_settings.product_views) {
      setViewSettings(companySettings.system_settings.product_views);
    }
  }, [companySettings]);

  // Listen for settings updates from SystemSettingsPage
  useEffect(() => {
    const handleSettingsUpdate = (event) => {
      console.log('ProductsPageLazy: Settings updated event:', event.detail);
      if (event.detail && event.detail.system_settings && event.detail.system_settings.product_views) {
        setViewSettings(event.detail.system_settings.product_views);
      }
    };

    window.addEventListener('settingsUpdated', handleSettingsUpdate);
    
    return () => {
      window.removeEventListener('settingsUpdated', handleSettingsUpdate);
    };
  }, []);

  // --- State variables with localStorage persistence ---
  const [viewMode, setViewMode] = useState(() => {
    try {
      const saved = localStorage.getItem(PRODUCT_PAGE_STATE_KEY);
      return saved ? JSON.parse(saved).viewMode : 'grid';
    } catch { return 'grid'; }
  });
  
  const [sortField, setSortField] = useState(() => {
    try {
      const saved = localStorage.getItem(PRODUCT_PAGE_STATE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.sortField) return parsed.sortField;
      }
    } catch { }
    return 'name';
  });

  // Update sort from settings when loaded (only if user hasn't customized)
  const defaultSortApplied = useRef(false);
  useEffect(() => {
    if (!defaultSortApplied.current && viewSettings.defaultSort) {
      const saved = localStorage.getItem(PRODUCT_PAGE_STATE_KEY);
      const userCustomized = saved && JSON.parse(saved).sortField;
      if (!userCustomized) {
        setSortField(viewSettings.defaultSort);
      }
      defaultSortApplied.current = true;
    }
  }, [viewSettings.defaultSort]);
  
  const [sortDirection, setSortDirection] = useState(() => {
    try {
      const saved = localStorage.getItem(PRODUCT_PAGE_STATE_KEY);
      return saved ? JSON.parse(saved).sortDirection : 'asc';
    } catch { return 'asc'; }
  });
  
  const [categoryFilter, setCategoryFilter] = useState(() => {
    // URL param takes precedence over localStorage
    if (urlCategoryFilter) return urlCategoryFilter;
    try {
      const saved = localStorage.getItem(PRODUCT_PAGE_STATE_KEY);
      return saved ? JSON.parse(saved).categoryFilter : '';
    } catch { return ''; }
  });

  const [badgeFilter, setBadgeFilter] = useState(() => {
    // URL param takes precedence over localStorage
    if (urlBadgeFilter) return urlBadgeFilter;
    try {
      const saved = localStorage.getItem(PRODUCT_PAGE_STATE_KEY);
      return saved ? JSON.parse(saved).badgeFilter : '';
    } catch { return ''; }
  });

  // --- Effect to save state whenever persisted variables change ---
  useEffect(() => {
    try {
      const stateToSave = {
        viewMode,
        sortField,
        sortDirection,
        categoryFilter,
        badgeFilter,
        pageSize,
      };
      localStorage.setItem(PRODUCT_PAGE_STATE_KEY, JSON.stringify(stateToSave));
    } catch (err) {
      console.error('Failed to save product page state to localStorage:', err);
    }
  }, [viewMode, sortField, sortDirection, categoryFilter, badgeFilter, pageSize]);

  // --- Effect to auto-open filters on mobile when any filter is applied ---
  useEffect(() => {
    const hasNonDefaultSort = sortField && sortField !== 'name';
    const hasNonDefaultDirection = sortDirection !== 'asc';
    const hasBadgeFilter = badgeFilter && badgeFilter !== '';

    if (hasNonDefaultSort || hasNonDefaultDirection || hasBadgeFilter) {
      setShowFilters(true);
    }
  }, [sortField, sortDirection, badgeFilter]);

  // --- Effect to handle URL search parameter on mount ---
  useEffect(() => {
    if (urlSearchQuery) {
      setSearchQuery(urlSearchQuery);
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('search');
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [urlSearchQuery, setSearchQuery, searchParams, setSearchParams]);

  // --- Effect to handle URL filter parameters (category and/or badge) ---
  useEffect(() => {
    if (urlCategoryFilter || urlBadgeFilter) {
      // Apply filters from URL - supports combined filters
      if (urlCategoryFilter) {
        setCategoryFilter(urlCategoryFilter);
      } else {
        setCategoryFilter('');
        localStorage.removeItem('categoryFilter');
      }

      if (urlBadgeFilter) {
        setBadgeFilter(urlBadgeFilter);
      } else {
        setBadgeFilter('');
        localStorage.removeItem('badgeFilter');
      }

      // Reset search when navigating via URL parameters
      setSearchQuery('');

      // Remove filter params from URL after applying
      const url = new URL(window.location.href);
      url.searchParams.delete('category');
      url.searchParams.delete('badge');
      window.history.replaceState({}, '', url.toString());
    }
  }, [urlCategoryFilter, urlBadgeFilter, setSearchQuery]);

  // Build filters object
  const buildFilters = useCallback(() => {
    const filters = {};
    if (searchQuery) filters.search = searchQuery;
    if (categoryFilter) filters.category = categoryFilter;
    if (badgeFilter) filters.badge = badgeFilter;
    if (hideOutOfStock) filters.hideOutOfStock = 'true';
    if (hideZeroPrice) filters.hideZeroPrice = 'true';
    if (catalogCustomerId) filters.customer_id = catalogCustomerId;

    // Use lightweight catalog serializer and filter only enabled products
    filters.fields = 'catalog';
    filters.status = 'enabled';

    // Add sorting parameters (badge sorting is handled client-side)
    if (searchQuery && sortField === 'name' && sortDirection === 'asc') {
      filters.ordering = 'relevance';
    } else if (sortField !== 'badge') {
      let orderingField = sortField;
      if (sortField === 'category__name') {
        orderingField = 'category__name';
      }
      filters.ordering = sortDirection === 'desc' ? `-${orderingField}` : orderingField;
    } else {
      filters.ordering = 'name';
    }

    return filters;
  }, [searchQuery, categoryFilter, badgeFilter, sortField, sortDirection, hideOutOfStock, hideZeroPrice, catalogCustomerId]);

  // --- Fetch Logic ---
  const paginationEnabled = viewSettings.paginationEnabled;

  const fetchProducts = useCallback(async (page = 1, append = false) => {
    const requestId = ++fetchRequestIdRef.current;
    if (!append) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    setError(null);

    try {
      const filters = buildFilters();
      const result = await productService.getProductsPaginated(page, paginationEnabled ? pageSize : DEFAULT_PAGE_SIZE, filters);
      if (requestId !== fetchRequestIdRef.current) return;

      if (result.success) {
        if (append) {
          let hitLimit = false;
          setProducts(prev => {
            const newProducts = [...prev, ...result.data];
            if (newProducts.length > MAX_PRODUCTS_IN_MEMORY) {
              hitLimit = true;
              return prev;
            }
            return newProducts;
          });
          if (hitLimit) {
            setHasMore(false);
          } else {
            setHasMore(result.hasNext);
            setCurrentPage(page);
          }
        } else {
          setProducts(result.data);
          setHasMore(result.hasNext);
          setCurrentPage(result.currentPage);
        }
        setTotalPages(result.totalPages);
        setTotalCount(result.count);
      } else {
        setError(result.error || "Failed to fetch products");
      }
    } catch (err) {
      if (requestId !== fetchRequestIdRef.current) return;
      console.error("[ProductsPageLazy] Error during product fetch:", err);
      setError("An unexpected error occurred while fetching products.");
    } finally {
      if (requestId === fetchRequestIdRef.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, [buildFilters, pageSize, paginationEnabled]);

  // Infinite scroll observer (only when pagination is disabled)
  const lastProductElementRef = useCallback(node => {
    if (paginationEnabled || loading || loadingMore) return;
    if (observerRef.current) observerRef.current.disconnect();
    observerRef.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore && !loadingMore) {
        fetchProducts(currentPage + 1, true);
      }
    });
    if (node) observerRef.current.observe(node);
  }, [paginationEnabled, loading, loadingMore, hasMore, fetchProducts, currentPage]);

  // Cleanup observer on unmount
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      setProducts([]);
    };
  }, []);

  // --- Fetch all categories (wait for settings) ---
  useEffect(() => {
    if (settingsLoading) return;
    const fetchCategories = async () => {
      try {
        const result = await productService.getCategories({ hideOutOfStock: hideOutOfStock, hideZeroPrice: hideZeroPrice, customerId: catalogCustomerId });


        if (result.success && result.data) {
          // Filter categories to only include those with products
          const categoriesWithProducts = result.data.filter(cat => cat.product_count > 0);

          setCategories(categoriesWithProducts);
        }
      } catch (err) {
        console.error('[ProductsPageLazy] Error fetching categories:', err);
        // Set empty array on error to prevent undefined issues
        setCategories([]);
      }
    };

    fetchCategories();
  }, [settingsLoading, hideOutOfStock, hideZeroPrice, catalogCustomerId]);

  // --- Fetch all badges ---
  useEffect(() => {
    const fetchBadges = async () => {
      try {
        const result = await productService.getBadges();
        if (result.success && result.data) {
          // Only show badges that have products assigned
          const badgesWithProducts = result.data.filter(badge => badge.product_count > 0);
          setBadges(badgesWithProducts);
        }
      } catch (err) {
        console.error('[ProductsPageLazy] Error fetching badges:', err);
        setBadges([]);
      }
    };

    fetchBadges();
  }, []);

  // --- Fetch banners for catalog_top location ---
  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const result = await bannerService.getActiveBanners('catalog_top', catalogCustomerId);

        if (result.success) {
          const bannersData = result.banners || result.data || [];
          // Ensure bannersData is actually an array before setting
          if (Array.isArray(bannersData)) {
            setBanners(bannersData);
          } else {
            console.error('[ProductsPageLazy] Banners data is not an array:', typeof bannersData);
            setBanners([]);
          }
          if (result.locationSettings) {
            setBannerLocationSettings(result.locationSettings);
          }
        }
      } catch (err) {
        console.error('[ProductsPageLazy] Error fetching banners:', err);
        setBanners([]);
      }
    };

    fetchBanners();
  }, [catalogCustomerId]);

  // --- Initial Load (wait for settings before first fetch) ---
  useEffect(() => {
    if (settingsLoading) return;
    setCurrentPage(1);
    setHasMore(true);
    fetchProducts(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsLoading, searchQuery, categoryFilter, badgeFilter, sortField, sortDirection, pageSize, hideOutOfStock, hideZeroPrice, paginationEnabled, catalogCustomerId]);

  const sortedProducts = React.useMemo(() => {
    let result = products;

    // Sort by badge if selected (by badge position, then products without badges last)
    if (sortField === 'badge') {
      result = [...result].sort((a, b) => {
        const aBadges = a.badges || [];
        const bBadges = b.badges || [];

        // Products with badges come first (in ascending), products without badges go last
        const aHasBadges = aBadges.length > 0;
        const bHasBadges = bBadges.length > 0;

        if (aHasBadges && !bHasBadges) return sortDirection === 'asc' ? -1 : 1;
        if (!aHasBadges && bHasBadges) return sortDirection === 'asc' ? 1 : -1;
        if (!aHasBadges && !bHasBadges) return 0;

        // Both have badges - sort by lowest position badge
        const aMinPosition = aBadges[0]?.position ?? 999;
        const bMinPosition = bBadges[0]?.position ?? 999;

        // Sort by position (lower position first in ascending)
        if (aMinPosition !== bMinPosition) {
          return sortDirection === 'asc'
            ? aMinPosition - bMinPosition
            : bMinPosition - aMinPosition;
        }

        // If same position, sort by badge name
        const aFirstBadge = aBadges[0]?.name || '';
        const bFirstBadge = bBadges[0]?.name || '';
        return sortDirection === 'asc'
          ? aFirstBadge.localeCompare(bFirstBadge)
          : bFirstBadge.localeCompare(aFirstBadge);
      });
    }

    return result;
  }, [products, sortField, sortDirection]);

  // --- Handlers ---
  const handleSortChange = (e) => setSortField(e.target.value);
  const toggleSortDirection = () => setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
  const handleCategoryChange = (e) => {
    setCategoryFilter(e.target.value);
    setBadgeFilter(''); // Reset badge filter when category changes
    setCurrentPage(1);
    setProducts([]);
  };
  const handleCategoryTileSelect = (categoryId) => {
    setCategoryFilter(categoryId);
    setBadgeFilter('');
    setCurrentPage(1);
    setProducts([]);
  };
  const categoryCounts = React.useMemo(() => {
    if (!Array.isArray(categories) || categories.length === 0) return null;
    return categories.reduce((acc, category) => {
      acc[category.id] = category.product_count;
      return acc;
    }, {});
  }, [categories]);
  const handleBadgeChange = (e) => {
    setBadgeFilter(e.target.value);
    setCurrentPage(1);
    setProducts([]);
  };
  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    
    if (mode === 'list') {
      const isDesktop = window.innerWidth >= 768;
      if (isDesktop) {
        if (leftCollapsed) {
          toggleLeftSidebar();
        }
        if (!rightVisible) {
          toggleRightSidebar();
        }
      }
    }
  };
  const toggleFilters = () => setShowFilters(prev => !prev);
  const handleRefresh = () => {
    setProducts([]);
    setCurrentPage(1);
    setHasMore(true);
    fetchProducts(1);
  };
  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages || page === currentPage) return;
    setProducts([]);
    fetchProducts(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setCurrentPage(1);
  };

  // --- Render Logic ---
  if (loading && products.length === 0) {
    return (
      <div className="w-full h-full flex flex-col p-6">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4">Loading products...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && products.length === 0) {
    return (
      <div className="w-full h-full flex flex-col p-6">
        <div className="flex-1 flex items-center justify-center">
          <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 flex justify-between items-center" role="alert">
            <span>{error}</span>
            <button 
              onClick={handleRefresh} 
              className="ml-3 bg-red-500 hover:bg-red-700 text-white font-bold py-1 px-2 rounded text-sm"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  const showBanners = banners.length > 0 && can('home.section.banners', 'Banners');
  const showFiltersSection = can('home.section.filters', 'Filters');
  const showProductCount = can('home.badge.product_count', 'Product count');
  const showSwitchView = can('home.btn.switch_view', 'Switch view');

  return (
    <div className="w-full min-h-full flex flex-col">
      {/* Banner Slider */}
      {showBanners && (
        <div className="mb-1 px-2 pt-1 md:mb-4 md:px-6 md:pt-6">
          <BannerSlider
            banners={banners}
            locationSettings={bannerLocationSettings}
            autoPlay={true}
            interval={5000}
            showOnMobile={true}
            isMobile={isMobile}
            location="catalog_top"
            activeCategoryId={categoryFilter}
            onCategorySelect={handleCategoryTileSelect}
            categoryCounts={categoryCounts}
          />
        </div>
      )}

      {/* Desktop Filters */}
      <div className={`hidden md:flex justify-between items-center mb-3 px-6 ${!showBanners ? 'pt-6' : ''}`}>
        <div className="flex items-center">
          {showFiltersSection && (<>
          <label htmlFor="category-select" className="mr-2 mb-0 text-nowrap">Category:</label>
          <select
            id="category-select"
            className="form-select py-1 px-2 text-sm border rounded"
            style={{ width: 'auto', minWidth: '180px' }}
            value={categoryFilter}
            onChange={handleCategoryChange}
          >
            <option value="">All Categories</option>
            {Array.isArray(categories) ? categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.display_name} ({category.product_count})
              </option>
            )) : null}
          </select>
          {categoryFilter ? (
            <button
              onClick={() => setCategoryFilter('')}
              className="ml-1 px-1.5 py-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded border border-red-300 mr-3"
              title="Clear category"
            >
              <BsX className="text-lg" />
            </button>
          ) : <span className="mr-3"></span>}

          <label htmlFor="badge-select" className="mr-2 mb-0 text-nowrap">Badge:</label>
          <select
            id="badge-select"
            className="form-select py-1 px-2 text-sm border rounded"
            style={{ width: 'auto', minWidth: '140px' }}
            value={badgeFilter}
            onChange={handleBadgeChange}
          >
            <option value="">All Badges</option>
            {Array.isArray(badges) ? badges.map((badge) => (
              <option key={badge.id} value={badge.id}>
                {badge.name}
              </option>
            )) : null}
          </select>
          {badgeFilter ? (
            <button
              onClick={() => setBadgeFilter('')}
              className="ml-1 px-1.5 py-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded border border-red-300 mr-3"
              title="Clear badge"
            >
              <BsX className="text-lg" />
            </button>
          ) : <span className="mr-3"></span>}

          <label htmlFor="sort-select" className="mr-2 mb-0 text-nowrap">Sort By:</label>
          <select
            id="sort-select"
            className="form-select py-1 px-2 text-sm mr-2 border rounded"
            style={{ width: 'auto', minWidth: '140px' }}
            value={sortField}
            onChange={handleSortChange}
          >
            <option value="name">Name</option>
            <option value="price">Price</option>
            <option value="sku">SKU</option>
            <option value="category__name">Category</option>
            <option value="badge">Badge</option>
          </select>

          <button
            className="inline-flex items-center px-3 py-1 border border-gray-200 text-sm rounded mr-3 hover:bg-gray-100"
            onClick={toggleSortDirection}
            title={sortDirection === 'asc' ? 'Ascending' : 'Descending'}
          >
            {sortDirection === 'asc' ? <BsArrowUp /> : <BsArrowDown />}
          </button>
          </>)}

          {showProductCount && (
          <span className="text-gray-500 text-sm">
            {totalCount} products
          </span>
          )}
        </div>

        <div className="flex items-center">
          {error && !loading && (
            <span className="text-red-500 mr-2 text-sm">Error loading!</span>
          )}

          {/* View mode buttons - cycle through enabled views */}
          {(() => {
            const enabledViews = [];
            if (viewSettings.gridViewEnabled) enabledViews.push({ value: 'grid', label: 'Grid', icon: <BsGrid3X3GapFill/> });
            if (viewSettings.listViewEnabled) enabledViews.push({ value: 'list', label: 'List', icon: <BsListUl/> });
            if (viewSettings.wsListViewEnabled) enabledViews.push({ value: 'wslist', label: 'Wholesale', icon: <BsTable/> });
            
            if (enabledViews.length > 1 && showSwitchView) {
              const currentIndex = enabledViews.findIndex(view => view.value === viewMode);
              const nextIndex = (currentIndex + 1) % enabledViews.length;
              const nextView = enabledViews[nextIndex];
              
              return (
                <button
                  className="py-1 px-3 rounded text-sm border border-primary text-primary hover:bg-blue-50 flex items-center space-x-1"
                  onClick={() => handleViewModeChange(nextView.value)}
                  title={`Switch to ${nextView.label} View`}
                >
                  {nextView.icon}
                  <span className="hidden md:inline ml-1">{nextView.label}</span>
                </button>
              );
            }
            return null;
          })()}
          
          <button
            className="py-1 px-3 border border-gray-200 rounded text-sm ml-1 hover:bg-gray-100"
            onClick={handleRefresh}
            disabled={loading}
            title="Refresh Products"
          >
            {loading ? (
              <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent" role="status"></span>
            ) : (
              <BsArrowClockwise />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Filters - Sticky below header (mobile header is fixed at 56px) */}
      <div className="md:hidden sticky z-40 bg-gray-100 px-2 py-1" style={{ top: `${isMobile ? 56 : headerHeight}px` }}>
        <div className="grid grid-cols-1 gap-2">
          <div className="flex justify-between items-center">
            {showFiltersSection ? (<>
            <select
              id="category-select-mobile"
              className="form-select py-1 px-2 text-sm flex-1 min-w-0 w-0 border rounded"
              value={categoryFilter}
              onChange={handleCategoryChange}
              aria-label="Select Category"
            >
              <option value="">All Categories</option>
              {Array.isArray(categories) ? categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.display_name} ({category.product_count})
                </option>
              )) : null}
            </select>
            {categoryFilter ? (
              <button
                onClick={() => setCategoryFilter('')}
                className="ml-1 px-1.5 py-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded border border-red-300 mr-1"
                title="Clear category"
              >
                <BsX className="text-lg" />
              </button>
            ) : <span className="mr-1"></span>}
            </>) : <span className="flex-1"></span>}

            <div className="flex items-center flex-shrink-0">
              {/* Mobile View mode buttons - cycle through enabled views */}
              {(() => {
                const enabledViews = [];
                if (viewSettings.gridViewEnabled) enabledViews.push({ value: 'grid', label: 'Grid', icon: <BsGrid3X3GapFill/> });
                if (viewSettings.listViewEnabled) enabledViews.push({ value: 'list', label: 'List', icon: <BsListUl/> });
                if (viewSettings.wsListViewEnabled) enabledViews.push({ value: 'wslist', label: 'Wholesale', icon: <BsTable/> });

                if (enabledViews.length > 1 && showSwitchView) {
                  const currentIndex = enabledViews.findIndex(view => view.value === viewMode);
                  const nextIndex = (currentIndex + 1) % enabledViews.length;
                  const nextView = enabledViews[nextIndex];

                  return (
                    <button
                      className="p-2 rounded text-sm border border-primary text-primary flex items-center space-x-1"
                      onClick={() => handleViewModeChange(nextView.value)}
                      title={`Switch to ${nextView.label} View`}
                    >
                      {nextView.icon}
                    </button>
                  );
                }
                return null;
              })()}

              <button
                className="p-2 border border-gray-200 rounded text-sm ml-1"
                onClick={handleRefresh}
                disabled={loading}
                title="Refresh Products"
              >
                {loading ? (
                  <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent" role="status"></span>
                ) : (
                  <BsArrowClockwise />
                )}
              </button>

              {(showFiltersSection || showProductCount) && (
              <button
                className={`p-2 rounded text-sm ml-1 ${showFilters
                  ? 'bg-gray-600 text-white'
                  : 'border border-gray-200 text-gray-700'}`}
                onClick={toggleFilters}
                aria-label="Toggle Filters"
                aria-expanded={showFilters}
              >
                <BsFilter/>
              </button>
              )}
            </div>
          </div>

          {showFilters && (showFiltersSection || showProductCount) && (
            <div className="col-span-1">
              <div className="bg-white border rounded shadow">
                <div className="p-2">
                  {showProductCount && (
                  <div className="flex justify-end mb-1">
                    <small className="text-gray-500">
                      {totalCount} products
                    </small>
                  </div>
                  )}
                  {showFiltersSection && (
                  <div className="grid grid-cols-2 gap-2">
                    {/* Sort By - 50% */}
                    <div>
                      <label htmlFor="mobile-sort-select" className="text-sm mb-1 block">Sort By:</label>
                      <div className="flex items-center">
                        <select
                          id="mobile-sort-select"
                          className="form-select py-1 px-2 text-sm flex-1 min-w-0 w-0 border rounded"
                          value={sortField}
                          onChange={handleSortChange}
                          aria-label="Select Sort Field"
                        >
                          <option value="name">Name</option>
                          <option value="price">Price</option>
                          <option value="sku">SKU</option>
                          <option value="category__name">Category</option>
                          <option value="badge">Badge</option>
                        </select>

                        <button
                          className="ml-1 p-1 border border-gray-200 rounded flex-shrink-0 hover:bg-gray-100"
                          onClick={toggleSortDirection}
                          title={sortDirection === 'asc' ? 'Ascending' : 'Descending'}
                          aria-label={sortDirection === 'asc' ? 'Sort Ascending' : 'Sort Descending'}
                        >
                          {sortDirection === 'asc' ? <BsArrowUp /> : <BsArrowDown />}
                        </button>
                      </div>
                    </div>

                    {/* Badge Filter - 50% */}
                    <div>
                      <label htmlFor="badge-select-filter" className="text-sm mb-1 block">Badge:</label>
                      <div className="flex items-center">
                        <select
                          id="badge-select-filter"
                          className="form-select py-1 px-2 text-sm flex-1 min-w-0 w-0 border rounded"
                          value={badgeFilter}
                          onChange={handleBadgeChange}
                          aria-label="Filter by Badge"
                        >
                          <option value="">All Badges</option>
                          {Array.isArray(badges) ? badges.map((badge) => (
                            <option key={badge.id} value={badge.id}>
                              {badge.name}
                            </option>
                          )) : null}
                        </select>
                        {badgeFilter && (
                          <button
                            onClick={() => setBadgeFilter('')}
                            className="ml-1 p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded border border-red-300"
                            title="Clear badge filter"
                          >
                            <BsX className="text-lg" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Products Display */}
      <div className="flex-1 flex flex-col px-2 pb-4 pt-1 md:px-6 md:pb-6 md:pt-0">
        {sortedProducts.length === 0 && !loading ? (
          <div className="bg-blue-100 border-l-4 border-blue-500 text-blue-700 p-4 text-center" role="alert">
            No products found matching your criteria.
          </div>
        ) : (
          <>
            {(!viewSettings.gridViewEnabled && !viewSettings.listViewEnabled && !viewSettings.wsListViewEnabled) ? (
              <div className="bg-orange-100 border-l-4 border-orange-500 text-orange-700 p-4 text-center" role="alert">
                <p className="font-semibold">No display modes are enabled</p>
                <p className="text-sm">Please enable at least one view mode in System Settings → Inventory & Products</p>
              </div>
            ) : (
              <div className={`flex-1 transition-opacity ${loading ? 'opacity-50 pointer-events-none' : ''}`}>
                {viewMode === "grid" && viewSettings.gridViewEnabled ? (
                  <GridView
                    products={sortedProducts}
                    isMobile={isMobile}
                    leftCollapsed={leftCollapsed}
                    rightVisible={rightVisible}
                    lastProductRef={!paginationEnabled ? lastProductElementRef : undefined}
                    showStock={showStock}
                  />
                ) : viewMode === "list" && viewSettings.listViewEnabled ? (
                  <ListView
                    products={sortedProducts}
                    isMobile={isMobile}
                    lastProductRef={!paginationEnabled ? lastProductElementRef : undefined}
                    showStock={showStock}
                  />
                ) : viewMode === "wslist" && viewSettings.wsListViewEnabled ? (
                  <WsListView
                    products={sortedProducts}
                    isMobile={isMobile}
                    lastProductRef={!paginationEnabled ? lastProductElementRef : undefined}
                    showStock={showStock}
                    hoverZoomEnabled={viewSettings.hoverZoomEnabled}
                  />
                ) : viewSettings.gridViewEnabled ? (
                  <GridView
                    products={sortedProducts}
                    isMobile={isMobile}
                    leftCollapsed={leftCollapsed}
                    rightVisible={rightVisible}
                    lastProductRef={!paginationEnabled ? lastProductElementRef : undefined}
                    showStock={showStock}
                  />
                ) : viewSettings.listViewEnabled ? (
                  <ListView
                    products={sortedProducts}
                    isMobile={isMobile}
                    lastProductRef={!paginationEnabled ? lastProductElementRef : undefined}
                    showStock={showStock}
                  />
                ) : viewSettings.wsListViewEnabled ? (
                  <WsListView
                    products={sortedProducts}
                    isMobile={isMobile}
                    lastProductRef={!paginationEnabled ? lastProductElementRef : undefined}
                    showStock={showStock}
                    hoverZoomEnabled={viewSettings.hoverZoomEnabled}
                  />
                ) : (
                  <div className="text-center py-12">
                    <p className="text-gray-500">No view mode is currently enabled. Please check your settings.</p>
                  </div>
                )}
              </div>
            )}

            {/* Infinite scroll indicators (when pagination is disabled) */}
            {!paginationEnabled && loadingMore && (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                <p className="mt-2 text-sm text-gray-500">Loading more products...</p>
              </div>
            )}
            {!paginationEnabled && !hasMore && products.length > 0 && (
              <div className="text-center py-4 text-gray-500 text-sm">
                All {totalCount} products loaded
              </div>
            )}

            {/* Pagination controls (when pagination is enabled) */}
            {paginationEnabled && totalCount > 0 && (
              <div className="bg-white border-t px-6 py-4 flex flex-col md:flex-row justify-between items-center">
                <div className="mb-3 md:mb-0 text-center md:text-left text-sm">
                  Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of{' '}
                  <span className="font-bold text-primary">{totalCount}</span> products
                </div>

                <div className="flex flex-col md:flex-row items-center">
                  {totalPages > 1 && (
                    <div className="flex mb-3 md:mb-0">
                      <button
                        className="px-3 py-1 border border-primary text-primary rounded-l hover:bg-primary hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1 || loading}
                      >
                        Previous
                      </button>

                      {(() => {
                        const pages = [];
                        const maxButtons = isMobile ? 3 : 5;
                        const startPage = Math.max(1, Math.min(currentPage - Math.floor(maxButtons / 2), totalPages - maxButtons + 1));
                        const endPage = Math.min(startPage + maxButtons - 1, totalPages);

                        for (let i = startPage; i <= endPage; i++) {
                          pages.push(
                            <button
                              key={`page-${i}`}
                              className={`px-3 py-1 border-t border-b ${i === currentPage ? 'bg-primary text-white' : 'border-primary text-primary hover:bg-primary hover:text-white'} transition-colors`}
                              onClick={() => handlePageChange(i)}
                              disabled={loading}
                            >
                              {i}
                            </button>
                          );
                        }

                        return pages;
                      })()}

                      <button
                        className="px-3 py-1 border border-primary text-primary rounded-r hover:bg-primary hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages || loading}
                      >
                        Next
                      </button>
                    </div>
                  )}

                  <div className={totalPages > 1 ? "md:ml-3" : ""}>
                    <select
                      className="w-auto px-3 py-2 border border-gray-200 rounded-md focus:ring-primary focus:border-primary"
                      value={pageSize}
                      onChange={handlePageSizeChange}
                    >
                      <option value="100">100 per page</option>
                      <option value="200">200 per page</option>
                      <option value="500">500 per page</option>
                    </select>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ProductsPageLazy;